// Payment-Seite im Aivy OS mit drei Modi.
// - create (customer/:partnerId/payment/new?from=<paymentId>) ist ein Entwurf,
//   vorbelegt aus der Vorlage oder dem aktuellen Payment. Geschrieben wird
//   erst beim Speichern, und zwar immer ein neuer Datensatz.
// - edit-plan (customer/payment/:id mit Paket) ändert die Paket-Zahlung, ein
//   Paketwechsel ist als Korrektur erlaubt.
// - edit-legacy (customer/payment/:id ohne Paket) ändert die Altzahlung wie
//   bisher. Sie wird nie zur Paket-Zahlung, „Auf Paket umstellen" legt einen
//   neuen Datensatz an. Die Altzahlung bleibt so der Rückweg, solange die
//   Pricing-Sperre aus ist.
// plan wird nur gelesen und geschrieben, wenn die Schema-Probe das Feld
// meldet. Sonst lehnt AppSync die ganze Query ab. Scheitert die Probe, bleibt
// Speichern gesperrt, bis sie klappt.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ExclamationTriangleIcon, HomeIcon } from '@heroicons/react/20/solid'
import { Switch } from '@headlessui/react'
import { useQuery, useQueryClient } from 'react-query'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CurrencyInput from 'react-currency-input-field'
import 'react-date-picker/dist/DatePicker.css'
import 'react-calendar/dist/Calendar.css'
import DatePicker from 'react-date-picker'
import { endOfDay, startOfDay } from 'date-fns'
import { isEqual } from 'lodash'

import { mutation, query } from '../api'
import Button from '../components/Button'
import Loader from '../components/Loader'
import Modal from '../components/Modal'
import AddonFields, {
  applyCareerCountChange
} from '../components/pricing/AddonFields'
import PlanPresetSelector, {
  NO_PLAN_CHOICE,
  choiceName
} from '../components/pricing/PlanPresetSelector'
import PresetDiff from '../components/pricing/PresetDiff'
import PricingEnforcementBadge from '../components/pricing/PricingEnforcementBadge'
import SchemaProbeNotice, {
  SCHEMA_PROBE_NOTICE_ID
} from '../components/pricing/SchemaProbeNotice'
import UsageCheck, {
  DSGVO_CONFIRM_ID,
  ENFORCEMENT_LOADING,
  draftPaymentOf
} from '../components/pricing/UsageCheck'
import {
  AVAILABILITY,
  CUSTOM_PLAN,
  PLANS,
  PLAN_ORDER,
  PRODUCT_AVAILABILITY,
  UNLIMITED,
  normalizePlanId,
  toStoredCount
} from '../constants/pricing-catalog'
import {
  PRODUCT_GROUPS,
  PRODUCT_KEYS,
  groupProducts,
  isManagedProduct,
  productDescription,
  productTitle,
  sortProducts
} from '../constants/products'
import { classNames, extractErrorMessage, uuidv4 } from '../helper'
import { STAGE, stageLabel } from '../helper/stage'
import {
  DSGVO_WARNING_KINDS,
  PAYMENT_KINDS,
  PLAN_CHOICES,
  PLAN_NAMES,
  UNKNOWN_PLAN_LABEL,
  applyPreset,
  automationCoverage,
  availableProductsFor,
  derivePresetDiff,
  describePlanValue,
  dsgvoAutomationWarning,
  effectiveLimitsOf,
  isUnlimited,
  paymentKind,
  paymentsAfterSave,
  storageFor
} from '../helper/pricing'
import { isConditionalCheckFailed } from '../helper/experiments'
import { pricingEnforcementState, pricingFlagOf } from '../helper/pricing-flag'
import { toCount } from '../helper/pricing-mirror/limits'
import {
  planOf,
  selectPayments
} from '../helper/pricing-mirror/payment-selection'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import { usePartnerAutomations } from '../hooks/use-pricing-usage'
import {
  SCHEMA_STATUS,
  useSchemaCapabilities
} from '../hooks/use-schema-capabilities'
import { useNotificationContext } from '../context/notification-context'

export const PAYMENT_MODES = {
  CREATE: 'create',
  EDIT_PLAN: 'edit-plan',
  EDIT_LEGACY: 'edit-legacy'
}

export const CREATED_MESSAGE =
  'Zahlung angelegt. Das Dashboard nutzt sie ab sofort für Laufzeit und Zugang.'
export const UPDATED_MESSAGE = 'Zahlung gespeichert'
export const ALREADY_CREATED_MESSAGE =
  'Die Zahlung war schon angelegt, ein früherer Versuch ist durchgegangen. Prüfe die Werte.'
export const STALE_PAYMENTS_MESSAGE =
  'Zahlungen konnten nicht neu geladen werden. Angezeigt wird der zuletzt geladene Stand.'

/**
 * Ob createPayment scheiterte, weil die id schon existiert. Das
 * GraphQL-Transformer-v2-Create prüft attribute_not_exists auf dem Key. Mit
 * einer festen id je Entwurf heißt das, ein früherer Versuch ist trotz Fehler
 * (Timeout, verlorene Antwort) durchgegangen.
 */
export const isAlreadyCreatedError = (err) => isConditionalCheckFailed(err)
export const NO_PLAN_SCHEMA_NOTICE =
  'Diese Umgebung kennt Payment.plan noch nicht. Pakete sind nach dem Backend-Deploy verfügbar.'
export const SCHEMA_FAILED_MESSAGE =
  'Schema-Prüfung fehlgeschlagen, erneut versuchen.'
export const STORED_PLAN_TRIAL_MESSAGE =
  'An dieser Zahlung ist ein Plan gespeichert, ein Trial ist deshalb gesperrt'
export const FIRST_PLAN_NOTICE =
  'Erst anlegen, wenn Backend und Dashboard dieser Umgebung live sind.'

const COUNT_FIELDS = ['career_count', 'seat_count', 'spaces_count']
const DATE_FIELDS = ['start_at', 'expires_at']

export const COUNT_INPUTS = [
  {
    field: 'career_count',
    resource: 'careers',
    label: 'Gleichzeitig offene Stellen',
    help: 'Aktive, nicht archivierte Stellen. Entwürfe zählen nicht.'
  },
  {
    field: 'seat_count',
    resource: 'seats',
    label: 'Dashboard-Zugänge',
    help: 'Teammitglieder mit Zugang zum Dashboard, eingeladene eingeschlossen.'
  },
  {
    field: 'spaces_count',
    resource: 'talentStorage',
    label: 'Talentspeicher',
    legacyLabel: 'Talentspeicher (im Altvertrag oft Testungen pro Jahr)',
    help: 'Gespeicherte Talente. Platz wird nur durch Löschen frei.'
  }
]

// ---------------------------------------------------------------------------
// Reine Helfer (exportiert für Tests)
// ---------------------------------------------------------------------------

/**
 * Modus der Seite. Ein Plan an einem Trial und ein unbekannter Plan zählen
 * wie im Dashboard nicht, das Payment ist dann ein Altvertrag.
 */
export const resolveMode = ({ isCreate, payment }) => {
  if (isCreate) return PAYMENT_MODES.CREATE
  return planOf(payment) ? PAYMENT_MODES.EDIT_PLAN : PAYMENT_MODES.EDIT_LEGACY
}

const toDate = (value) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const toIso = (value) => {
  const date = toDate(value)
  return date ? date.toISOString() : null
}

const priceValue = (cents) => {
  const n = Number(cents)
  return cents !== null && cents !== undefined && Number.isFinite(n)
    ? n / 100
    : 0
}

const toCents = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(String(value).replace(',', '.'))
  return Number.isFinite(n) ? Math.round(n * 100) : null
}

// Gespeicherte Menge für das Formular. 0 bleibt beim Bearbeiten sichtbar,
// damit die Validierung greift wie bisher.
const countValue = (value) => {
  const n = toCount(value)
  return n === null ? '' : toStoredCount(n)
}

const prefillCount = (value) => {
  const n = toCount(value)
  return n === null || n <= 0 ? '' : toStoredCount(n)
}

const productList = (products) =>
  Array.isArray(products) ? products.filter(Boolean) : []

// Bei Paket-Zahlungen heißen leere Mengen „Paketwert". Das Formular zeigt
// dann den Wert, mit dem das Dashboard rechnet.
const planCounts = (payment, prefill) => {
  const read = prefill ? prefillCount : countValue
  const counts = {
    career_count: read(payment?.career_count),
    seat_count: read(payment?.seat_count),
    spaces_count: read(payment?.spaces_count)
  }
  if (!planOf(payment)) return counts

  const limits = effectiveLimitsOf(payment)
  COUNT_INPUTS.forEach(({ field, resource }) => {
    const current = counts[field]
    if (current !== '' && current > 0) return
    const stored = toStoredCount(limits[resource])
    if (stored !== null) counts[field] = stored
  })
  return counts
}

/**
 * Startwerte des Formulars.
 * - Bearbeiten nimmt das Payment, wie es gespeichert ist.
 * - Anlegen nimmt Produkte (alle, auch nicht angezeigte), Mengen, Preis und
 *   Vertragsende aus der Vorlage, dazu den Plan, wenn die Vorlage eine
 *   Paket-Zahlung ist. Vertragsbeginn ist heute, Trial ist aus.
 *
 * @param {{ mode: string, payment?: object, source?: object, now?: Date }} params
 */
export const initialValuesFor = ({
  mode,
  payment = null,
  source = null,
  now = new Date()
}) => {
  if (mode !== PAYMENT_MODES.CREATE) {
    return {
      plan: mode === PAYMENT_MODES.EDIT_PLAN ? planOf(payment) : null,
      no_plan: false,
      is_trial: !!payment?.is_trial,
      products: productList(payment?.products),
      price: priceValue(payment?.price),
      ...planCounts(payment, false),
      start_at: toDate(payment?.start_at),
      expires_at: toDate(payment?.expires_at)
    }
  }

  return {
    plan: planOf(source),
    no_plan: false,
    is_trial: false,
    products: productList(source?.products),
    price: priceValue(source?.price),
    ...planCounts(source, true),
    start_at: startOfDay(now),
    expires_at: toDate(source?.expires_at)
  }
}

const emptyToUndefined = (value, original) =>
  original === '' || original === null || original === undefined
    ? undefined
    : value

// formatCount zeigt ab UNLIMITED „unbegrenzt“, hier zählt die Zahl
const UNLIMITED_LABEL = new Intl.NumberFormat('de-DE').format(UNLIMITED)

const countSchema = (label) =>
  Yup.number()
    .transform(emptyToUndefined)
    .typeError(`${label} muss eine Zahl sein`)
    .integer(`${label} muss eine ganze Zahl sein`)
    .min(1, `${label} muss mindestens 1 sein`)
    .max(UNLIMITED, `${label} darf höchstens ${UNLIMITED_LABEL} sein`)
    .required(`${label} ist erforderlich`)

/**
 * Validierung je Modus. Paket-Pflicht gibt es nur, wenn das Schema plan kennt.
 * Beim Anlegen erfüllt „Kein Paket" die Pflicht.
 * Ohne bestätigte Schema-Probe (schemaConfirmed false) ist unklar, ob die
 * Zahlung ein Paket hat. Dann ist Speichern gesperrt, und Trial lässt sich
 * nicht neu setzen, damit plan und is_trial nie zusammen entstehen.
 *
 * Ist an der Zahlung schon ein plan gespeichert (auch ein unbekannter),
 * lässt sich Trial ebenfalls nicht neu setzen.
 *
 * Ein gespeichertes Datum lässt sich beim Bearbeiten nicht leeren. Daten
 * gehen nie als null raus (buildUpdateInput), das Leeren hätte also nichts
 * bewirkt.
 *
 * @param {{ mode: string, paymentPlan: boolean, schemaConfirmed?: boolean,
 *   initialTrial?: boolean, storedPlan?: boolean,
 *   storedDates?: { start_at?: boolean, expires_at?: boolean },
 *   now?: () => Date }} params
 */
export const buildValidationSchema = ({
  mode,
  paymentPlan,
  schemaConfirmed = true,
  initialTrial = false,
  storedPlan = false,
  storedDates = {},
  now = () => new Date()
}) => {
  const isCreate = mode === PAYMENT_MODES.CREATE
  const requirePlan =
    !!paymentPlan && (isCreate || mode === PAYMENT_MODES.EDIT_PLAN)
  const requireDates = mode !== PAYMENT_MODES.EDIT_LEGACY

  const dateSchema = (message, field) =>
    requireDates
      ? Yup.date().nullable().typeError('Ungültiges Datum').required(message)
      : Yup.date()
          .nullable()
          .typeError('Ungültiges Datum')
          .test(
            'keep-stored-date',
            'Ein gespeichertes Datum lässt sich nicht leeren',
            (value) => !!value || !storedDates[field]
          )

  return Yup.object({
    plan: Yup.string()
      .nullable()
      .test('schema-confirmed', SCHEMA_FAILED_MESSAGE, () => !!schemaConfirmed)
      .test(
        'plan-required',
        isCreate ? 'Wähle ein Paket oder „Kein Paket"' : 'Wähle ein Paket',
        function (value) {
          if (!requirePlan || value) return true
          return isCreate && this.parent.no_plan === true
        }
      )
      .test(
        'plan-known',
        UNKNOWN_PLAN_LABEL,
        (value) => !value || PLAN_CHOICES.includes(value)
      ),
    is_trial: Yup.boolean()
      .test(
        'trial-without-plan',
        'Paket-Zahlungen sind keine Trials',
        function (value) {
          return !(value && this.parent.plan)
        }
      )
      .test(
        'trial-needs-schema',
        'Trial erst nach erfolgreicher Schema-Prüfung setzen',
        (value) => !value || !!schemaConfirmed || !!initialTrial
      )
      .test(
        'trial-with-stored-plan',
        STORED_PLAN_TRIAL_MESSAGE,
        (value) => !value || !storedPlan || !!initialTrial
      ),
    products: Yup.array().of(Yup.string()),
    price: Yup.number()
      .transform(emptyToUndefined)
      .typeError('Preis muss eine Zahl sein')
      .min(0, 'Preis darf nicht negativ sein')
      .required('Preis ist erforderlich'),
    career_count: countSchema('Gleichzeitig offene Stellen'),
    seat_count: countSchema('Dashboard-Zugänge'),
    spaces_count: countSchema('Talentspeicher'),
    start_at: dateSchema('Vertragsbeginn ist erforderlich', 'start_at'),
    expires_at: dateSchema('Vertragsende ist erforderlich', 'expires_at')
      .test(
        'after-start',
        'Vertragsende muss nach dem Vertragsbeginn liegen',
        function (value) {
          const start = toDate(this.parent.start_at)
          if (!value || !start) return true
          return new Date(value) > start
        }
      )
      .test(
        'plan-in-future',
        'Paket-Zahlungen brauchen ein Vertragsende in der Zukunft',
        function (value) {
          if (!this.parent.plan || !value) return true
          return new Date(value) > now()
        }
      )
  })
}

/**
 * Formularwerte in Payment-Felder. Mengen gehen über toStoredCount, ab
 * UNLIMITED also als 999999. plan nur mit withPlan, Paket-Zahlungen sind nie
 * Trials.
 */
export const toPaymentFields = (values, { withPlan = false } = {}) => {
  const plan = values?.plan || null
  const fields = {
    is_trial: plan ? false : !!values?.is_trial,
    products: sortProducts(productList(values?.products)),
    price: toCents(values?.price),
    career_count: toStoredCount(values?.career_count),
    seat_count: toStoredCount(values?.seat_count),
    spaces_count: toStoredCount(values?.spaces_count),
    start_at: toIso(values?.start_at),
    expires_at: toIso(values?.expires_at)
  }
  if (withPlan) fields.plan = plan
  return fields
}

/**
 * Input für createPayment wie bisher in customer.js, dazu plan, Produkte,
 * Preis in Cent, Mengen und Laufzeit. product_type, status und payment_type
 * bleiben leer (Stripe-Semantik, deletePartner liest status).
 */
export const buildCreateInput = ({
  values,
  partnerId,
  withPlan,
  id = uuidv4(),
  now = new Date()
}) => {
  const timestamp = now.toISOString()
  const input = {
    id,
    partner_id: partnerId,
    createdAt: timestamp,
    updatedAt: timestamp,
    authorized_read: [`__read_${partnerId}`],
    ...toPaymentFields(values, { withPlan })
  }

  if (!input.plan) delete input.plan
  ;[...COUNT_FIELDS, ...DATE_FIELDS, 'price'].forEach((key) => {
    if (input[key] === null) delete input[key]
  })
  return input
}

export const createMutationFor = (input) =>
  input.plan ? 'createPaymentWithPlan' : 'createPayment'

/**
 * Input für updatePayment mit nur den geänderten Feldern. So bleiben Felder,
 * die das OS nicht kennt oder die Stripe gesetzt hat, unberührt. Altzahlungen
 * bekommen nie plan. Mengen und Daten gehen nie als null raus.
 */
export const buildUpdateInput = ({
  id,
  mode,
  values,
  initialValues,
  withPlan
}) => {
  const options = { withPlan: !!withPlan && mode === PAYMENT_MODES.EDIT_PLAN }
  const next = toPaymentFields(values, options)
  const before = toPaymentFields(initialValues, options)
  const input = { id }

  Object.keys(next).forEach((key) => {
    if (next[key] === null) return
    if (!isEqual(next[key], before[key])) input[key] = next[key]
  })
  return input
}

export const updateMutationFor = (mode) =>
  mode === PAYMENT_MODES.EDIT_PLAN ? 'updatePaymentWithPlan' : 'updatePayment'

const TAG_TONES = {
  green: 'bg-green-50 text-green-700 ring-green-600/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  gray: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/20'
}

/**
 * Tag eines Produkts relativ zum gewählten Paket. Nur verwaltete Produkte und
 * Katalog-Pakete haben einen Tag.
 *
 * @returns {{ label: string, tone: 'green'|'blue'|'gray'|'amber' }|null}
 */
export const productTag = (product, planId, selected) => {
  const target = normalizePlanId(planId)
  if (!PLAN_ORDER.includes(target) || !isManagedProduct(product)) return null

  const name = PLAN_NAMES[target]
  const availability = PRODUCT_AVAILABILITY[product][target]
  if (availability === AVAILABILITY.YES) {
    return { label: `In ${name} enthalten`, tone: 'green' }
  }
  if (availability === AVAILABILITY.ADDON) {
    return { label: 'Zubuchbar', tone: 'blue' }
  }
  return selected
    ? { label: 'Zusätzlich', tone: 'amber' }
    : { label: `Nicht in ${name}`, tone: 'gray' }
}

const BADGE_TONES = {
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-700/10',
  gray: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  red: 'bg-red-50 text-red-700 ring-red-600/10'
}

/**
 * Badge für die Art einer Zahlung, z. B. „Paket · Growth".
 *
 * @returns {{ label: string, tone: string }}
 */
export const paymentBadge = (payment) => {
  const kind = paymentKind(payment)
  if (kind === PAYMENT_KINDS.PLAN) {
    return { label: `Paket · ${PLAN_NAMES[planOf(payment)]}`, tone: 'indigo' }
  }
  if (kind === PAYMENT_KINDS.TRIAL) return { label: 'Trial', tone: 'amber' }
  if (kind === PAYMENT_KINDS.CREDITS) return { label: 'Credits', tone: 'gray' }
  if (kind === PAYMENT_KINDS.UNKNOWN_PLAN) {
    return { label: UNKNOWN_PLAN_LABEL, tone: 'red' }
  }
  return { label: 'Altvertrag', tone: 'gray' }
}

/**
 * DSGVO-Zustand der Zahlung im Formular. Maßgeblich ist der Stand nach dem
 * Speichern. inviteTalent plant Löschungen nur, solange ein Payment
 * AUTOMATIONS und ein Vertragsende in der Zukunft hat.
 * - Trägt diese Zahlung die Löschung selbst, oder trägt sie nach dem
 *   Speichern noch ein anderes Payment, blockiert nichts. Im zweiten Fall
 *   bleibt ein Hinweis mit dem Ende.
 * - Trägt nach dem Speichern kein Payment mehr, blockiert die Warnung, wenn
 *   die Löschung heute noch läuft (Speichern beendet sie) und immer beim
 *   Anlegen. Lief sie schon vorher nicht, bleibt es beim Bearbeiten ein
 *   Hinweis.
 * - Ist die Automatisierung des Kunden nicht lesbar, gilt dasselbe mit einer
 *   vorsichtigen Meldung.
 *
 * @param {{ mode: string, automations: object, products: string[],
 *   expiresAt?: Date|string|null, payments?: object[], paymentId?: string,
 *   now?: Date }} params
 * @returns {{ blocking: boolean, pending: boolean, message: string }|null}
 */
export const dsgvoStateFor = ({
  mode,
  automations,
  products,
  expiresAt = null,
  payments = [],
  paymentId = null,
  now = new Date()
}) => {
  const draft = {
    ...(paymentId ? { id: paymentId } : {}),
    products: productList(products),
    expires_at: expiresAt
  }
  const coverage = automationCoverage({ payments, draft, now })
  if (coverage.draftCovers) return null
  // Nach dem Speichern trägt ein anderes Payment, es steht nichts auf dem
  // Spiel. Der Hinweis mit dem Ende braucht die Automatisierung des Kunden.
  const unreadable = automations?.isLoading || automations?.isError
  if (coverage.coveredAfter && unreadable) return null

  const blocking = mode === PAYMENT_MODES.CREATE || coverage.coveredBefore

  if (automations?.isLoading) {
    return {
      blocking: false,
      pending: true,
      message: 'DSGVO-Automatisierung des Kunden wird geprüft.'
    }
  }

  if (automations?.isError) {
    return {
      blocking,
      pending: false,
      message:
        'Die DSGVO-Automatisierung des Kunden konnte nicht geladen werden. Nach dem Speichern trägt kein Payment mehr DSGVO-Automatisierung mit einem Vertragsende in der Zukunft, das Backend plant dann womöglich keine Löschungen mehr ein. Prüf die automatische Löschung beim Kunden, bevor du speicherst.'
    }
  }

  const warning = dsgvoAutomationWarning({
    partner: automations?.partner,
    payments,
    draft,
    now
  })
  if (!warning) return null

  return {
    blocking:
      warning.requiresConfirmation &&
      (mode === PAYMENT_MODES.CREATE ||
        warning.kind === DSGVO_WARNING_KINDS.STOPS),
    pending: false,
    message: warning.message
  }
}

/**
 * „Kein Paket" im Entwurf. Mengen und verwaltete Produkte gehen auf die
 * Vorlage zurück (die Zahlung, aus der der Entwurf vorbelegt wurde, sonst
 * leer), damit „Zahlen und Produkte wie bisher" stimmt. Produkte außerhalb
 * des Preismodells, Preis und Laufzeit bleiben, wie CS sie gesetzt hat.
 *
 * @param {object} values Formularwerte
 * @param {object|null} sourceValues initialValuesFor der Vorlage im Modus
 *   create
 */
export const applyNoPlan = (values, sourceValues) => {
  const unmanaged = productList(values?.products).filter(
    (product) => !isManagedProduct(product)
  )
  const managed = productList(sourceValues?.products).filter(isManagedProduct)

  return {
    ...values,
    plan: null,
    no_plan: true,
    products: sortProducts([...new Set([...unmanaged, ...managed])]),
    career_count: sourceValues?.career_count ?? '',
    seat_count: sourceValues?.seat_count ?? '',
    spaces_count: sourceValues?.spaces_count ?? ''
  }
}

const TEMPLATE_FIELDS = [...COUNT_FIELDS, 'products']

const templateOf = (values) =>
  TEMPLATE_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]:
        field === 'products'
          ? sortProducts(productList(values?.[field]))
          : (values?.[field] ?? '')
    }),
    {}
  )

// Ob CS seit der letzten Vorlage Mengen oder Produkte von Hand geändert hat
export const hasManualChanges = (values, template) =>
  !isEqual(templateOf(values), templateOf(template))

/**
 * Ob ein Wechsel der Paket-Karte erst bestätigt werden muss. Das gilt im
 * Modus edit-plan (gespeicherte Zahlung), wenn die Werte vom gewählten Paket
 * abweichen oder CS seit der letzten Vorlage etwas von Hand geändert hat.
 *
 * @param {{ mode: string, values: object, planId: string|null,
 *   template: object, available?: string[] }} params template sind die
 *   Werte direkt nach der letzten Vorlage (Paket, „Kein Paket" oder Start)
 */
export const planChangeNeedsConfirm = ({
  mode,
  values,
  planId,
  template,
  available
}) => {
  if (mode === PAYMENT_MODES.EDIT_PLAN) return true
  if (derivePresetDiff(values, planId, { available }).items.length > 0) {
    return true
  }
  return hasManualChanges(values, template)
}

/**
 * Erste Paket-Zahlung eines Kunden. Dann prüft das OS nicht, ob das Dashboard
 * der Umgebung Pakete schon versteht, CS bestätigt das einmal.
 */
export const isFirstPlanPayment = ({ mode, planId, payments }) =>
  mode === PAYMENT_MODES.CREATE &&
  !!planId &&
  !selectPayments(payments).planPayment

export const FIRST_PLAN_CONFIRM_ID = 'first-plan-confirm'

export const FIELD_LABELS = {
  plan: 'Paket',
  is_trial: 'Trial',
  price: 'Preis',
  career_count: 'Gleichzeitig offene Stellen',
  seat_count: 'Dashboard-Zugänge',
  spaces_count: 'Talentspeicher',
  start_at: 'Vertragsbeginn',
  expires_at: 'Vertragsende',
  products: 'Produkte'
}

// Sprungziele je Feld, in der Reihenfolge der Seite
export const FIELD_TARGETS = {
  plan: 'paket-auswahl',
  price: 'price',
  career_count: 'career_count',
  seat_count: 'seat_count',
  spaces_count: 'spaces_count',
  start_at: 'start_at-group',
  expires_at: 'expires_at-group',
  is_trial: 'is_trial',
  products: 'produkte'
}

const errorText = (error) =>
  Array.isArray(error) ? error.filter(Boolean).join(', ') : String(error)

/**
 * Einträge der Übersicht über dem Speichern-Button. Hürden (DSGVO, erste
 * Paket-Zahlung, Schema) stehen immer da, Feldfehler erst nach einem
 * Speicherversuch.
 *
 * @param {{ errors: object, showFieldErrors: boolean, blockers?: object[],
 *   targets?: object }} params
 * @returns {{ id: string, label: string, message: string, target: string }[]}
 */
export const errorSummaryItems = ({
  errors = {},
  showFieldErrors,
  blockers = [],
  targets = FIELD_TARGETS
}) => {
  const fields = showFieldErrors
    ? Object.keys(FIELD_TARGETS)
        .filter((field) => errors[field])
        .map((field) => ({
          id: field,
          label: FIELD_LABELS[field],
          message: errorText(errors[field]),
          target: targets[field] || FIELD_TARGETS[field]
        }))
    : []
  return [...blockers, ...fields]
}

// Springt zu einem Feld der Seite und setzt den Fokus darauf
const jumpTo = (targetId) => {
  const target = document.getElementById(targetId)
  if (!target) return
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: 'center' })
  }
  // react-date-picker rendert zusätzlich ein verstecktes natives Feld
  const focusable = target.matches('input, button, select, textarea')
    ? target
    : target.querySelector(
        'input:not([disabled]):not([hidden]):not([type="hidden"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || target
  if (typeof focusable.focus === 'function') focusable.focus()
}

/**
 * Hinweise zu Vertragsbeginn und Vertragsende, nie blockierend.
 */
export const dateNotes = (values, { mode, now = new Date() } = {}) => {
  const notes = []
  const start = toDate(values?.start_at)
  const end = toDate(values?.expires_at)

  if (start && start > now) {
    notes.push(
      'Der Vertragsbeginn liegt in der Zukunft. Bis dahin meldet das Dashboard die Zahlung als noch nicht gestartet, bei einem Trial ist der Zugang so lange gesperrt.'
    )
  }
  if (end && end < now && !values?.plan) {
    notes.push(
      'Das Vertragsende liegt in der Vergangenheit. Bei einem Trial endet damit der Zugang, sonst meldet das Dashboard die Zahlung als abgelaufen.'
    )
  }
  if (mode === PAYMENT_MODES.EDIT_LEGACY && (!start || !end)) {
    notes.push(
      'Ohne Vertragsbeginn und Vertragsende meldet das Dashboard ungültige Daten.'
    )
  }
  return notes
}

// Rückfallwert, wenn CS „Unbegrenzt" wieder abwählt
const countFallback = (field, values) => {
  const target = normalizePlanId(values?.plan)
  const plan = PLANS[target]
  if (!plan || target === 'unlimited') return ''
  if (field === 'career_count') return plan.limits.careers
  if (field === 'seat_count') return plan.limits.seats

  const careers = Number(values?.career_count)
  return (
    storageFor(
      target,
      Number.isFinite(careers) && !isUnlimited(careers) ? careers : undefined
    ) ?? ''
  )
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

const inputClass =
  'block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm sm:leading-6'

const Badge = ({ label, tone }) => (
  <span
    className={classNames(
      'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
      BADGE_TONES[tone] || BADGE_TONES.gray
    )}
  >
    {label}
  </span>
)

const FieldError = ({ children }) =>
  children ? <div className='mt-1 text-sm text-red-600'>{children}</div> : null

const Section = ({ id, title, description, children }) => (
  <section
    id={id}
    className='border-b border-gray-900/10 pb-10 pt-10 first:pt-0'
  >
    <h2 className='text-base font-semibold leading-7 text-gray-900'>{title}</h2>
    {description && (
      <p className='mt-1 max-w-2xl text-sm leading-6 text-gray-600'>
        {description}
      </p>
    )}
    <div className='mt-4 space-y-4'>{children}</div>
  </section>
)

const Notice = ({ tone = 'gray', children }) => (
  <div
    className={classNames(
      'rounded-md p-3 text-sm',
      tone === 'amber' && 'bg-amber-50 text-amber-900',
      tone === 'red' && 'bg-red-50 text-red-800',
      tone === 'indigo' && 'bg-indigo-50 text-indigo-900',
      tone === 'gray' && 'bg-gray-50 text-gray-700'
    )}
  >
    {children}
  </div>
)

const ProductGrid = ({ values, planId, available, onToggle }) => {
  const selected = productList(values.products)
  const groups = groupProducts([...PRODUCT_KEYS, ...selected])

  return (
    <div className='space-y-8'>
      {groups.map((group) => (
        <div key={group.id}>
          <h3 className='text-sm font-semibold text-gray-900'>{group.label}</h3>
          {group.id === PRODUCT_GROUPS.UNKNOWN.id ? (
            <div className='mt-3'>
              <p className='text-sm text-gray-600'>
                Diese Werte stehen an der Zahlung, das OS kennt sie nicht. Sie
                bleiben beim Speichern erhalten.
              </p>
              <div className='mt-2 flex flex-wrap gap-2'>
                {group.products.map((product) => (
                  <Badge key={product} label={product} tone='gray' />
                ))}
              </div>
            </div>
          ) : (
            <div className='mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
              {group.products.map((product) => {
                const checked = selected.includes(product)
                const inSchema = available.includes(product)
                const tag = inSchema
                  ? productTag(product, planId, checked)
                  : { label: 'Noch nicht im Schema', tone: 'gray' }

                return (
                  <label
                    key={product}
                    htmlFor={`product-${product}`}
                    className={classNames(
                      checked
                        ? 'border-indigo-600 ring-2 ring-indigo-600'
                        : 'border-gray-300',
                      inSchema
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-60',
                      'relative flex gap-x-3 rounded-lg border bg-white p-4 shadow-sm'
                    )}
                  >
                    <input
                      id={`product-${product}`}
                      type='checkbox'
                      className='mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
                      checked={checked}
                      disabled={!inSchema}
                      onChange={() => onToggle(product)}
                    />
                    <span className='flex flex-1 flex-col'>
                      <span className='flex flex-wrap items-center gap-2'>
                        <span className='text-sm font-medium text-gray-900'>
                          {productTitle(product)}
                        </span>
                        {tag && (
                          <span
                            className={classNames(
                              'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset',
                              TAG_TONES[tag.tone]
                            )}
                          >
                            {tag.label}
                          </span>
                        )}
                      </span>
                      <span className='mt-1 text-sm text-gray-500'>
                        {productDescription(product)}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Formular
// ---------------------------------------------------------------------------

const FirstPlanNotice = ({ confirmed, onConfirmedChange }) => (
  <div
    id='first-plan-notice'
    className='rounded-lg border border-amber-300 bg-amber-50 p-4'
    data-testid='first-plan-notice'
  >
    <div className='flex items-start gap-x-2'>
      <ExclamationTriangleIcon
        className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500'
        aria-hidden='true'
      />
      <div>
        <h3 className='text-sm font-semibold text-amber-900'>
          Erste Paket-Zahlung für diesen Kunden
        </h3>
        <p className='mt-1 text-sm font-medium text-amber-900'>
          {FIRST_PLAN_NOTICE}
        </p>
        <p className='mt-1 text-sm text-amber-800'>
          Das OS sieht nicht, welcher Dashboard-Stand auf {stageLabel(STAGE)}{' '}
          läuft. Ein Dashboard ohne Paket-Logik rechnet mit der neuesten Zahlung
          und setzt Produkte, Zugänge und Laufzeit dieser Zahlung sofort durch.
          Die Reihenfolge je Umgebung ist Backend, dann Dashboard, dann Pakete
          im OS. Sie steht in der README im Abschnitt Pricing.
        </p>
        <label className='mt-3 flex items-start gap-x-2 text-sm font-medium text-amber-900'>
          <input
            id={FIRST_PLAN_CONFIRM_ID}
            type='checkbox'
            className='mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-600'
            checked={!!confirmed}
            onChange={(event) => onConfirmedChange(event.target.checked)}
          />
          <span>Backend und Dashboard sind auf {stageLabel(STAGE)} live.</span>
        </label>
      </div>
    </div>
  </div>
)

const PlanChangeDialog = ({
  pending,
  isEditPlan,
  diffItems,
  manualChanges,
  onApply,
  onKeep,
  onCancel
}) => {
  const isNoPlan = pending.choice === NO_PLAN_CHOICE
  const isCustom = pending.choice === CUSTOM_PLAN
  const name = isNoPlan ? '„Kein Paket"' : choiceName(pending.choice)

  const effect = isNoPlan
    ? 'Zahlen und Produkte des Preismodells gehen auf die Vorlage zurück. Deine Anpassungen daran gehen verloren.'
    : isCustom
      ? 'Zahlen und Produkte bleiben, wie sie sind.'
      : `${name} übernehmen setzt Stellen, Zugänge, Talentspeicher und Produkte auf das Paket. Anpassungen behalten wechselt nur das Paket.`

  return (
    <div data-testid='plan-change-dialog'>
      <h3 className='text-base font-semibold leading-6 text-gray-900'>
        Auf {name} wechseln?
      </h3>
      <div className='mt-3 space-y-2 text-sm text-gray-700'>
        {isEditPlan && (
          <p>
            Diese Zahlung ist gespeichert. Der Wechsel ändert sie, sobald du
            speicherst.
          </p>
        )}
        {diffItems.length > 0 && (
          <div>
            <p>Deine Werte weichen vom bisherigen Paket ab.</p>
            <ul className='mt-1 list-inside list-disc text-gray-600'>
              {diffItems.slice(0, 8).map((item) => (
                <li key={`${item.field}-${item.message}`}>{item.message}</li>
              ))}
            </ul>
          </div>
        )}
        {diffItems.length === 0 && manualChanges && (
          <p>Du hast Mengen oder Produkte von Hand geändert.</p>
        )}
        <p>{effect}</p>
      </div>
      <div className='mt-6 flex flex-wrap justify-end gap-3'>
        <Button.SecondaryLG text='Abbrechen' onClick={onCancel} />
        {!isNoPlan && !isCustom && (
          <Button.SecondaryLG text='Anpassungen behalten' onClick={onKeep} />
        )}
        <Button.PrimaryLG
          text={
            isNoPlan
              ? 'Auf Vorlage zurücksetzen'
              : isCustom
                ? 'Wechseln'
                : `${name} übernehmen`
          }
          onClick={onApply}
        />
      </div>
    </div>
  )
}

const ErrorSummary = ({ items, summaryRef }) => (
  <div
    ref={summaryRef}
    tabIndex={-1}
    role='alert'
    className='mt-6 rounded-md bg-red-50 p-4 ring-1 ring-inset ring-red-600/20'
    data-testid='error-summary'
  >
    <p className='text-sm font-semibold text-red-900'>
      {items.length === 1
        ? '1 Angabe prüfen'
        : `${items.length} Angaben prüfen`}
    </p>
    <ul className='mt-2 space-y-1 text-sm text-red-800'>
      {items.map((item) => (
        <li key={item.id}>
          <button
            type='button'
            onClick={() => jumpTo(item.target)}
            className='font-semibold text-red-900 underline hover:text-red-700'
          >
            {item.label}
          </button>
          <span className='block'>{item.message}</span>
        </li>
      ))}
    </ul>
  </div>
)

const PaymentForm = ({
  mode,
  payment,
  partnerId,
  customer,
  customerName,
  payments,
  source,
  fromMissing,
  capabilities,
  automations
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { success, error } = useNotificationContext()
  const { flagsQuery } = useFeatureFlags()

  const isCreate = mode === PAYMENT_MODES.CREATE
  const isLegacy = mode === PAYMENT_MODES.EDIT_LEGACY
  const isEditPlan = mode === PAYMENT_MODES.EDIT_PLAN
  const withPlan = capabilities.paymentPlan
  const schemaFailed = capabilities.status === SCHEMA_STATUS.FAILED
  const available = useMemo(
    () => availableProductsFor({ products: capabilities.products }),
    [capabilities.products]
  )

  const [now] = useState(() => new Date())
  // Eine id je Entwurf. Ein zweiter Klick nach einem mehrdeutigen Fehler legt
  // so kein zweites Payment an, das Create scheitert dann an der id.
  const [draftId] = useState(() => uuidv4())
  const [dsgvoConfirmed, setDsgvoConfirmed] = useState(false)
  const [firstPlanConfirmed, setFirstPlanConfirmed] = useState(false)
  const [pendingPlan, setPendingPlan] = useState(null)
  const summaryRef = useRef(null)
  const handledSubmitRef = useRef(0)

  const initialValues = useMemo(
    () => initialValuesFor({ mode, payment, source, now }),
    [mode, payment, source, now]
  )
  // Werte der Vorlage für „Kein Paket", also wie bisher
  const sourceValues = useMemo(
    () => initialValuesFor({ mode: PAYMENT_MODES.CREATE, source, now }),
    [source, now]
  )
  const [template, setTemplate] = useState(initialValues)

  // Auch ein unbekannter oder an einem Trial ignorierter plan zählt
  const storedPlan =
    !isCreate && describePlanValue(payment?.plan).kind !== 'none'
  const validationSchema = useMemo(
    () =>
      buildValidationSchema({
        mode,
        paymentPlan: withPlan,
        schemaConfirmed: capabilities.status === SCHEMA_STATUS.READY,
        initialTrial: !!initialValues.is_trial,
        storedPlan,
        storedDates: {
          start_at: !isCreate && !!initialValues.start_at,
          expires_at: !isCreate && !!initialValues.expires_at
        }
      }),
    [
      mode,
      withPlan,
      capabilities.status,
      initialValues.is_trial,
      initialValues.start_at,
      initialValues.expires_at,
      isCreate,
      storedPlan
    ]
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const dsgvo = dsgvoStateFor({
        mode,
        automations,
        products: values.products,
        expiresAt: values.expires_at,
        payments,
        paymentId: payment?.id,
        now
      })
      if (dsgvo?.pending || (dsgvo?.blocking && !dsgvoConfirmed)) {
        error('Bestätige zuerst den Hinweis zur DSGVO-Automatisierung.')
        setSubmitting(false)
        return
      }

      try {
        if (isCreate) {
          const input = buildCreateInput({
            values,
            partnerId,
            withPlan,
            id: draftId
          })
          try {
            await mutation({ mutation: createMutationFor(input), input })
            success(CREATED_MESSAGE)
          } catch (err) {
            if (!isAlreadyCreatedError(err)) throw err
            success(ALREADY_CREATED_MESSAGE)
          }
          // Erst wechseln, dann neu laden. Sonst rechnet der Entwurf kurz mit
          // der neuen Zahlung als Vorlage.
          navigate(`/customer/payment/${input.id}`, { replace: true })
          queryClient.invalidateQueries('payment')
          return
        }

        const input = buildUpdateInput({
          id: payment.id,
          mode,
          values,
          initialValues,
          withPlan
        })
        await mutation({ mutation: updateMutationFor(mode), input })
        await queryClient.invalidateQueries('payment')
        success(UPDATED_MESSAGE)
      } catch (err) {
        error(extractErrorMessage(err))
      } finally {
        setSubmitting(false)
      }
    }
  })

  const {
    values,
    errors,
    touched,
    submitCount,
    isSubmitting,
    setFieldValue,
    setValues
  } = formik
  const showError = (field) =>
    (touched[field] || submitCount > 0) && errors[field] ? errors[field] : null

  const planId = normalizePlanId(values.plan)
  const planChoice = planId || (values.no_plan ? NO_PLAN_CHOICE : null)
  const presetDiff = derivePresetDiff(values, planId, { available })

  const dsgvo = dsgvoStateFor({
    mode,
    automations,
    products: values.products,
    expiresAt: values.expires_at,
    payments,
    paymentId: payment?.id,
    now
  })
  const dsgvoBlocked =
    !!dsgvo?.pending || (!!dsgvo?.blocking && !dsgvoConfirmed)

  // Verschwindet die Warnung durch eine Änderung am Entwurf, gilt die
  // Bestätigung nicht mehr. Kommt sie wieder, muss CS neu bestätigen.
  const dsgvoBlocking = !!dsgvo?.blocking
  useEffect(() => {
    if (!dsgvoBlocking) setDsgvoConfirmed(false)
  }, [dsgvoBlocking])

  const { planPayment } = useMemo(() => selectPayments(payments), [payments])
  const notes = dateNotes(values, { mode, now })

  const firstPlan = isFirstPlanPayment({ mode, planId, payments })
  const firstPlanBlocked = firstPlan && !firstPlanConfirmed

  // Status der Pricing-Sperre heute und nach dem Speichern dieser Zahlung
  const flag = flagsQuery.isError ? undefined : pricingFlagOf(flagsQuery.data)
  const flagCtx = {
    stage: STAGE,
    // lead_ampel steht nicht im PARTNER-Set. Ohne das Feld meldet die
    // Auswertung bei Targeting darauf partial statt eines falschen Ergebnisses.
    partner:
      customer && Object.prototype.hasOwnProperty.call(customer, 'lead_ampel')
        ? customer
        : undefined
  }
  const currentEnforcement = pricingEnforcementState({
    flag,
    partnerId,
    payments,
    ...flagCtx
  })
  const draft = draftPaymentOf({
    values,
    planId: isLegacy ? null : planId,
    paymentId: payment?.id,
    createdAt: payment?.createdAt
  })
  const draftEnforcement = flagsQuery.isLoading
    ? ENFORCEMENT_LOADING
    : pricingEnforcementState({
        flag,
        partnerId,
        payments: paymentsAfterSave({ payments, draft, now }),
        ...flagCtx
      })

  const applyChoice = (choice) => {
    const next =
      choice === NO_PLAN_CHOICE
        ? applyNoPlan(values, sourceValues)
        : { ...applyPreset(values, choice, { available }), no_plan: false }
    setValues(next)
    setTemplate(next)
  }

  const keepChoice = (choice) => {
    setValues({ ...values, plan: choice, no_plan: false, is_trial: false })
  }

  const choosePlan = (choice) => {
    if (choice === planChoice || pendingPlan) return
    const needsConfirm = planChangeNeedsConfirm({
      mode,
      values,
      planId,
      template,
      available
    })
    if (needsConfirm) {
      setPendingPlan({ choice })
      return
    }
    applyChoice(choice)
  }

  const toggleProduct = (product) => {
    const list = productList(values.products)
    setFieldValue(
      'products',
      list.includes(product)
        ? list.filter((p) => p !== product)
        : sortProducts([...list, product])
    )
  }

  const toggleUnlimited = (field) => {
    setFieldValue(
      field,
      isUnlimited(values[field]) ? countFallback(field, values) : UNLIMITED
    )
  }

  // Gleiche Regel wie der Stepper Zusatzstellen, der Talentspeicher rechnet
  // mit, solange CS ihn nicht von Hand gesetzt hat
  const changeCareerCount = (event) => {
    const parsed = parseFloat(event.target.value)
    setValues(
      applyCareerCountChange(values, planId, Number.isNaN(parsed) ? '' : parsed)
    )
  }

  const trialBlockedByPlan = storedPlan && !initialValues.is_trial
  const trialLocked =
    !!planId ||
    (isCreate && withPlan && !values.no_plan) ||
    (schemaFailed && !initialValues.is_trial) ||
    trialBlockedByPlan
  const trialHint = planId
    ? 'Paket-Zahlungen sind keine Trials.'
    : schemaFailed && !initialValues.is_trial
      ? 'Die Schema-Prüfung ist fehlgeschlagen. Ob die Zahlung ein Paket hat, ist unklar, deshalb ist Trial gesperrt.'
      : trialBlockedByPlan
        ? `${STORED_PLAN_TRIAL_MESSAGE}.`
        : trialLocked
          ? 'Wähle „Kein Paket", um einen Trial anzulegen.'
          : 'Gibt an, ob die Zahlung ein Trial ist. Ein Trial verliert mit dem Vertragsende den Zugang.'

  const blockers = [
    schemaFailed && {
      id: 'schema',
      label: 'Schema-Prüfung',
      message: SCHEMA_FAILED_MESSAGE,
      target: SCHEMA_PROBE_NOTICE_ID
    },
    dsgvo?.pending && {
      id: 'dsgvo-pending',
      label: 'DSGVO-Automatisierung',
      message: 'Wird noch geprüft, Speichern geht gleich.',
      target: DSGVO_CONFIRM_ID
    },
    !dsgvo?.pending &&
      dsgvoBlocked && {
        id: 'dsgvo',
        label: 'DSGVO-Automatisierung',
        message: 'Bestätige zuerst den Hinweis zur DSGVO-Automatisierung.',
        target: DSGVO_CONFIRM_ID
      },
    firstPlanBlocked && {
      id: 'first-plan',
      label: 'Erste Paket-Zahlung',
      message:
        'Bestätige, dass Backend und Dashboard dieser Umgebung live sind.',
      target: FIRST_PLAN_CONFIRM_ID
    }
  ].filter(Boolean)

  const summaryItems = errorSummaryItems({
    errors,
    showFieldErrors: submitCount > 0,
    blockers,
    targets: withPlan ? FIELD_TARGETS : { ...FIELD_TARGETS, plan: 'paket' }
  })

  // Nach einem gescheiterten Speicherversuch springt der Fokus auf die
  // Übersicht direkt über dem Button
  useEffect(() => {
    if (isSubmitting || submitCount === handledSubmitRef.current) return
    handledSubmitRef.current = submitCount
    if (Object.keys(errors).length === 0) return
    const summary = summaryRef.current
    if (!summary) return
    if (typeof summary.scrollIntoView === 'function') {
      summary.scrollIntoView({ block: 'center' })
    }
    summary.focus()
  }, [submitCount, isSubmitting, errors])

  const canSave =
    !isSubmitting &&
    (isCreate || formik.dirty) &&
    !dsgvoBlocked &&
    !firstPlanBlocked &&
    !schemaFailed

  const badge = isCreate
    ? planId || values.no_plan || !withPlan
      ? paymentBadge({ plan: values.plan, is_trial: values.is_trial })
      : null
    : paymentBadge(payment)
  const storedPlanValue = describePlanValue(payment?.plan)

  const pages = [
    { name: 'Kunden', to: '/customers' },
    { name: customerName || 'Kunde', to: `/customer/${partnerId}` },
    { name: isCreate ? 'Neue Zahlung' : 'Zahlung', to: '#', current: true }
  ]

  const dateLabelClass = 'block text-sm font-medium leading-6 text-gray-900'

  return (
    <div>
      <nav className='mb-8 flex' aria-label='Breadcrumb'>
        <ol className='flex items-center space-x-4'>
          <li>
            <Link to='/' className='text-gray-400 hover:text-gray-500'>
              <HomeIcon className='h-5 w-5 flex-shrink-0' aria-hidden='true' />
              <span className='sr-only'>Home</span>
            </Link>
          </li>
          {pages.map((page) => (
            <li key={page.name}>
              <div className='flex items-center'>
                <svg
                  className='h-5 w-5 flex-shrink-0 text-gray-300'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  aria-hidden='true'
                >
                  <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
                </svg>
                <Link
                  to={page.to}
                  className='ml-4 text-sm font-medium text-gray-500 hover:text-gray-700'
                  aria-current={page.current ? 'page' : undefined}
                >
                  {page.name}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      <header className='mb-8 flex flex-wrap items-center gap-3'>
        <h1 className='text-3xl font-bold leading-tight tracking-tight text-gray-900'>
          {isCreate
            ? `Neue Zahlung für ${customerName || 'Kunde'}`
            : `Zahlung für ${customerName || 'Kunde'}`}
        </h1>
        {isCreate && <Badge label='Entwurf' tone='gray' />}
        {badge && <Badge {...badge} />}
      </header>

      <Section
        id='paket'
        title='Paket'
        description={
          isLegacy
            ? null
            : 'Wähle ein Paket als Vorlage. Zahlen und Produkte kannst du danach frei anpassen.'
        }
      >
        {schemaFailed ? (
          <SchemaProbeNotice
            onRetry={capabilities.refetch}
            isRetrying={capabilities.isFetching}
          >
            Ob diese Umgebung Payment.plan kennt, ist unklar. Die Zahlung ist
            deshalb ohne plan geladen, Pakete und Speichern bleiben gesperrt,
            bis die Prüfung klappt.
          </SchemaProbeNotice>
        ) : (
          !withPlan && <Notice tone='amber'>{NO_PLAN_SCHEMA_NOTICE}</Notice>
        )}

        <div>
          <h3 className='text-sm font-medium text-gray-500'>
            Pricing-Sperre für diesen Kunden
          </h3>
          <PricingEnforcementBadge
            className='mt-2'
            state={currentEnforcement}
            planName={planPayment ? PLAN_NAMES[planOf(planPayment)] : null}
            showDetail
            isLoading={flagsQuery.isLoading}
            onRetry={() => flagsQuery.refetch()}
            isRetrying={flagsQuery.isFetching}
          />
        </div>

        {isLegacy && (
          <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
            <h3 className='text-sm font-semibold text-gray-900'>
              {paymentKind(payment) === PAYMENT_KINDS.TRIAL
                ? 'Trial ohne Paket'
                : 'Altvertrag ohne Paket'}
            </h3>
            <p className='mt-1 text-sm text-gray-600'>
              Diese Zahlung bleibt, wie sie ist, und dient als Rückweg, solange
              die Pricing-Sperre aus ist. Für ein Paket legst du eine neue
              Zahlung an, die Werte werden übernommen.
            </p>
            {storedPlanValue.kind === 'unknown' && (
              <p className='mt-2 text-sm text-red-700'>
                {`Gespeichert ist der Plan „${storedPlanValue.raw}", den das Dashboard nicht kennt. Es behandelt die Zahlung deshalb als Altvertrag.`}
              </p>
            )}
            <button
              type='button'
              disabled={!withPlan}
              onClick={() =>
                navigate(
                  `/customer/${partnerId}/payment/new?from=${payment.id}`
                )
              }
              className='mt-3 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40'
            >
              Auf Paket umstellen
            </button>
          </div>
        )}

        {isEditPlan && (
          <Notice>
            Ein Paketwechsel ändert diese Zahlung direkt. Nutze das nur zur
            Korrektur. Für einen neuen Vertrag legst du eine{' '}
            <Link
              to={`/customer/${partnerId}/payment/new?from=${payment.id}`}
              className='font-semibold text-indigo-600 hover:text-indigo-500'
            >
              neue Zahlung aus dieser
            </Link>{' '}
            an.
          </Notice>
        )}

        {isCreate && fromMissing && (
          <Notice tone='amber'>
            Die Vorlage gehört nicht zu diesem Kunden. Vorbelegt ist deshalb die
            aktuelle Zahlung.
          </Notice>
        )}

        {withPlan && (
          <div id='paket-auswahl'>
            <PlanPresetSelector
              value={planChoice}
              onChange={choosePlan}
              allowNoPlan={isCreate}
              disabled={isLegacy}
              customized={presetDiff.customized}
              error={showError('plan')}
            />
          </div>
        )}

        {firstPlan && (
          <FirstPlanNotice
            confirmed={firstPlanConfirmed}
            onConfirmedChange={setFirstPlanConfirmed}
          />
        )}

        {isCreate && values.no_plan && payments.length > 0 && (
          <Notice tone='amber'>
            {planPayment
              ? 'Der Kunde hat schon eine Paket-Zahlung. Eine neue Zahlung ohne Paket wird bei ausgeschalteter Pricing-Sperre zum Altvertrag und steuert Laufzeit und Zugang.'
              : 'Eine neue Zahlung ohne Paket wird zum neuesten Altvertrag. Produkte, Zugänge und Laufzeit kommen ab dem Speichern von ihr, auch ohne Pricing-Sperre.'}
          </Notice>
        )}

        {withPlan && !isLegacy && (
          <>
            <AddonFields planId={planId} values={values} onChange={setValues} />
            <PresetDiff
              planId={planId}
              values={values}
              available={available}
              onReset={(next) => {
                setValues(next)
                setTemplate(next)
              }}
            />
          </>
        )}

        <UsageCheck
          partnerId={partnerId}
          planId={isLegacy ? null : planId}
          values={values}
          payments={payments}
          paymentId={payment?.id}
          createdAt={payment?.createdAt}
          enforcement={draftEnforcement}
          dsgvo={dsgvo && !dsgvo.pending ? dsgvo : null}
          dsgvoConfirmed={dsgvoConfirmed}
          onDsgvoConfirmedChange={setDsgvoConfirmed}
        />
      </Section>

      <Section
        title='Basisdaten'
        description='Preis, Mengen und Laufzeit. Mengen ab 999.999 gelten als unbegrenzt.'
      >
        <div className='grid grid-cols-1 gap-8 sm:grid-cols-2'>
          <div>
            <label
              htmlFor='price'
              className='block text-sm font-medium leading-6 text-gray-900'
            >
              Preis / Monat (netto)
            </label>
            <div className='mt-2'>
              <CurrencyInput
                id='price'
                name='price'
                suffix=' €'
                placeholder='Preis pro Monat'
                className={inputClass}
                value={values.price}
                onBlur={formik.handleBlur}
                decimalsLimit={2}
                onValueChange={(value) => setFieldValue('price', value ?? '')}
              />
              <FieldError>{showError('price')}</FieldError>
            </div>
          </div>

          {COUNT_INPUTS.map(({ field, label, legacyLabel, help }) => {
            const unlimited = isUnlimited(values[field])
            return (
              <div key={field}>
                <div className='flex items-center justify-between gap-x-4'>
                  <label
                    htmlFor={field}
                    className='block text-sm font-medium leading-6 text-gray-900'
                  >
                    {isLegacy && legacyLabel ? legacyLabel : label}
                  </label>
                  <label className='flex items-center gap-x-2 text-sm text-gray-700'>
                    <input
                      type='checkbox'
                      aria-label={`${label} unbegrenzt`}
                      className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
                      checked={unlimited}
                      onChange={() => toggleUnlimited(field)}
                    />
                    Unbegrenzt
                  </label>
                </div>
                <div className='mt-2'>
                  <input
                    type='number'
                    min={1}
                    step={1}
                    id={field}
                    name={field}
                    value={unlimited ? '' : values[field]}
                    placeholder={unlimited ? 'Unbegrenzt' : undefined}
                    disabled={unlimited}
                    onChange={
                      field === 'career_count'
                        ? changeCareerCount
                        : formik.handleChange
                    }
                    onBlur={formik.handleBlur}
                    className={inputClass}
                  />
                  <p className='mt-1 text-xs text-gray-500'>{help}</p>
                  <FieldError>{showError(field)}</FieldError>
                </div>
              </div>
            )
          })}

          <div
            id='start_at-group'
            role='group'
            aria-labelledby='start_at-label'
          >
            <span id='start_at-label' className={dateLabelClass}>
              Vertragsbeginn
            </span>
            <DatePicker
              id='start_at'
              className='mt-2'
              locale='de-DE'
              value={values.start_at}
              dayAriaLabel='Tag des Vertragsbeginns'
              monthAriaLabel='Monat des Vertragsbeginns'
              yearAriaLabel='Jahr des Vertragsbeginns'
              calendarAriaLabel='Kalender für den Vertragsbeginn öffnen'
              clearAriaLabel='Vertragsbeginn leeren'
              nativeInputAriaLabel='Vertragsbeginn'
              onChange={(value) =>
                setFieldValue('start_at', value ? startOfDay(value) : null)
              }
            />
            <FieldError>{showError('start_at')}</FieldError>
          </div>

          <div
            id='expires_at-group'
            role='group'
            aria-labelledby='expires_at-label'
          >
            <span id='expires_at-label' className={dateLabelClass}>
              Vertragsende
            </span>
            <DatePicker
              id='expires_at'
              className='mt-2'
              locale='de-DE'
              value={values.expires_at}
              dayAriaLabel='Tag des Vertragsendes'
              monthAriaLabel='Monat des Vertragsendes'
              yearAriaLabel='Jahr des Vertragsendes'
              calendarAriaLabel='Kalender für das Vertragsende öffnen'
              clearAriaLabel='Vertragsende leeren'
              nativeInputAriaLabel='Vertragsende'
              onChange={(value) =>
                setFieldValue('expires_at', value ? endOfDay(value) : null)
              }
            />
            <FieldError>{showError('expires_at')}</FieldError>
          </div>

          <div className='sm:col-span-2'>
            <Switch.Group
              as='div'
              className='flex items-center justify-between gap-x-6'
            >
              <span className='flex flex-grow flex-col'>
                <Switch.Label
                  as='span'
                  className='text-sm font-medium leading-6 text-gray-900'
                  passive
                >
                  Trial
                </Switch.Label>
                <Switch.Description as='span' className='text-sm text-gray-500'>
                  {trialHint}
                </Switch.Description>
              </span>
              <Switch
                id='is_trial'
                checked={!!values.is_trial}
                disabled={trialLocked}
                onChange={(checked) => setFieldValue('is_trial', checked)}
                className={classNames(
                  values.is_trial ? 'bg-indigo-600' : 'bg-gray-200',
                  trialLocked
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                )}
              >
                <span
                  aria-hidden='true'
                  className={classNames(
                    values.is_trial ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                  )}
                />
              </Switch>
            </Switch.Group>
            <FieldError>{showError('is_trial')}</FieldError>
          </div>
        </div>

        {notes.map((note) => (
          <Notice key={note} tone='amber'>
            {note}
          </Notice>
        ))}
      </Section>

      <Section
        id='produkte'
        title='Produkte'
        description='Was der Kunde im Dashboard nutzen kann. Produkte außerhalb des Preismodells ändert ein Paket nicht.'
      >
        <ProductGrid
          values={values}
          planId={planId}
          available={available}
          onToggle={toggleProduct}
        />
      </Section>

      {summaryItems.length > 0 && (
        <ErrorSummary items={summaryItems} summaryRef={summaryRef} />
      )}

      <div className='mt-6 flex flex-wrap items-center justify-end gap-x-6 gap-y-2'>
        <button
          type='button'
          className='text-sm font-semibold leading-6 text-gray-900'
          onClick={() => navigate(-1)}
        >
          Abbrechen
        </button>
        <button
          type='button'
          disabled={!canSave}
          onClick={formik.handleSubmit}
          className='rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-30'
        >
          {isSubmitting ? 'lädt ...' : 'Speichern'}
        </button>
      </div>

      <Modal
        open={!!pendingPlan}
        setOpen={(open) => {
          if (!open) setPendingPlan(null)
        }}
        size='lg'
      >
        {pendingPlan && (
          <PlanChangeDialog
            pending={pendingPlan}
            isEditPlan={isEditPlan}
            diffItems={presetDiff.items}
            manualChanges={hasManualChanges(values, template)}
            onCancel={() => setPendingPlan(null)}
            onKeep={() => {
              keepChoice(pendingPlan.choice)
              setPendingPlan(null)
            }}
            onApply={() => {
              applyChoice(pendingPlan.choice)
              setPendingPlan(null)
            }}
          />
        )}
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const ErrorState = ({ message, onRetry }) => (
  <div className='rounded-md bg-red-50 p-4 text-sm text-red-800'>
    <p>{message}</p>
    {onRetry && (
      <button
        type='button'
        onClick={onRetry}
        className='mt-2 font-semibold text-red-900 underline'
      >
        Erneut versuchen
      </button>
    )}
  </div>
)

/**
 * Zwei Routen, eine Seite.
 * - /customer/payment/:id bearbeitet eine Zahlung
 * - /customer/:partnerId/payment/new?from=<paymentId> legt eine an
 */
const Payment = () => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const isCreate = !params.id && !!params.partnerId
  const paymentId = isCreate ? null : params.id
  const fromId = isCreate ? searchParams.get('from') : null

  const capabilities = useSchemaCapabilities()
  const withPlan = capabilities.paymentPlan
  const ready = !capabilities.isLoading

  const paymentQuery = useQuery({
    queryKey: ['payment', paymentId, { withPlan }],
    queryFn: () =>
      query({
        query: withPlan ? 'getPaymentWithPlan' : 'getPayment',
        variables: { id: paymentId }
      }),
    enabled: ready && !!paymentId,
    refetchOnWindowFocus: false
  })
  const payment = paymentQuery.data || null
  const partnerId = isCreate ? params.partnerId : payment?.partner_id

  const customerQuery = useQuery({
    queryKey: ['customer', partnerId],
    queryFn: () => query({ query: 'getPartner', variables: { id: partnerId } }),
    enabled: !!partnerId,
    refetchOnWindowFocus: false
  })

  const paymentsQuery = useQuery({
    queryKey: ['payment', 'by-partner', partnerId, { withPlan }],
    queryFn: () =>
      query({
        query: withPlan
          ? 'paymentByPartnerIdByCreatedAtWithPlan'
          : 'paymentByPartnerIdByCreatedAt',
        variables: { partner_id: partnerId }
      }),
    enabled: ready && !!partnerId,
    refetchOnWindowFocus: false
  })
  const paymentsData = paymentsQuery.data
  const payments = useMemo(
    () => (Array.isArray(paymentsData) ? paymentsData : []),
    [paymentsData]
  )

  const automations = usePartnerAutomations(partnerId)

  const fromPayment = useMemo(
    () => (fromId ? payments.find(({ id }) => id === fromId) || null : null),
    [fromId, payments]
  )
  const source = useMemo(() => {
    if (!isCreate) return null
    return fromPayment || selectPayments(payments).periodPayment
  }, [isCreate, fromPayment, payments])

  if (
    !ready ||
    (paymentId && paymentQuery.isLoading) ||
    (partnerId && (paymentsQuery.isLoading || customerQuery.isLoading))
  ) {
    return <Loader loadingText='Zahlung wird geladen ...' />
  }

  // Fehlerseite nur ohne Daten. react-query v3 meldet isError auch, wenn ein
  // Nachladen im Hintergrund scheitert und der letzte Stand noch da ist. Dann
  // bleibt das Formular stehen, darüber steht ein Hinweis.
  if (paymentId && paymentQuery.isError && !paymentQuery.data) {
    return (
      <ErrorState
        message={`Zahlung konnte nicht geladen werden. ${extractErrorMessage(paymentQuery.error)}`}
        onRetry={() => paymentQuery.refetch()}
      />
    )
  }

  if (paymentId && !payment) {
    return <ErrorState message='Zahlung nicht gefunden.' />
  }

  if (paymentsQuery.isError && !paymentsQuery.data) {
    return (
      <ErrorState
        message={`Zahlungen des Kunden konnten nicht geladen werden. ${extractErrorMessage(paymentsQuery.error)}`}
        onRetry={() => paymentsQuery.refetch()}
      />
    )
  }

  // Beim Anlegen kommt die partnerId roh aus der URL. Ohne lesbaren Kunden
  // entstünde ein Payment ohne Partner.
  if (isCreate && !customerQuery.data) {
    return customerQuery.isError ? (
      <ErrorState
        message={`Kunde konnte nicht geladen werden. ${extractErrorMessage(customerQuery.error)}`}
        onRetry={() => customerQuery.refetch()}
      />
    ) : (
      <ErrorState message='Kunde nicht gefunden.' />
    )
  }

  const mode = resolveMode({ isCreate, payment })
  const customer = customerQuery.data
  const customerName = customer?.display_name || customer?.name

  const staleQuery = [paymentQuery, paymentsQuery].find(
    (q) => q.isError && q.data
  )

  return (
    <>
      {staleQuery && (
        <div className='mb-4' data-testid='stale-payments'>
          <ErrorState
            message={`${STALE_PAYMENTS_MESSAGE} ${extractErrorMessage(staleQuery.error)}`}
            onRetry={() => staleQuery.refetch()}
          />
        </div>
      )}
      {/* Ohne capabilities.status im Key. Ein neuer Versuch der Probe hängt
          das Formular nicht aus, nur ein Wechsel von withPlan (anderer
          Modus) oder eine andere Vorlage startet es neu. */}
      <PaymentForm
        key={`${mode}:${paymentId || partnerId}:${fromId || ''}:${withPlan}`}
        mode={mode}
        payment={payment}
        partnerId={partnerId}
        customer={customer}
        customerName={customerName}
        payments={payments}
        source={source}
        fromMissing={!!fromId && !fromPayment}
        capabilities={capabilities}
        automations={automations}
      />
    </>
  )
}

export default Payment
